import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './StudentAssignmentsView.css';

function StudentAssignmentsView({ student }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, new, in-progress, submitted, graded, overdue
  const [expandedId, setExpandedId] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setAssignments([]);

      const classId = student.classId;
      if (!classId) {
        setLoading(false);
        return;
      }

      // Load assignments
      const assignmentsRef = collection(db, 'classes', classId, 'assignments');
      const assignmentsSnapshot = await getDocs(assignmentsRef);
      const assignmentsData = assignmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Load submissions for this student
      const submissionsData = [];
      for (const assignment of assignmentsData) {
        const submissionRef = collection(db, 'classes', classId, 'assignments', assignment.id, 'assignmentSubmissions');
        const submissionQuery = query(submissionRef, where('studentId', '==', student.studentId));
        const submissionSnapshot = await getDocs(submissionQuery);
        if (!submissionSnapshot.empty) {
          const submissionDoc = submissionSnapshot.docs[0];
          const submission = submissionDoc.data();
          submissionsData.push({
            assignmentId: assignment.id,
            submissionId: submissionDoc.id,
            ...submission,
          });
        }
      }

      // Merge assignments with submissions
      const mergedAssignments = assignmentsData.map(assignment => {
        const submission = submissionsData.find(s => s.assignmentId === assignment.id);
        return {
          ...assignment,
          submission: submission || null,
        };
      });

      // Sort by due date (newest first)
      mergedAssignments.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
      setAssignments(mergedAssignments);
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  }, [student.classId, student.studentId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const getSubmissionStatus = (assignment) => {
    if (!assignment.submission) return 'Assigned';
    const status = assignment.submission.status;
    if (status === 'Assigned') return 'Assigned';
    if (status === 'Started') return 'In Progress';
    if (status === 'Submitted') {
      const dueDate = new Date(assignment.dueDate);
      const submittedAt = new Date(assignment.submission.submittedAt);
      return submittedAt > dueDate ? 'Late' : 'Submitted';
    }
    if (status === 'Graded') return 'Graded';
    if (status === 'Returned') return 'Returned';
    return status;
  };

  const filteredAssignments = assignments.filter(assignment => {
    const status = getSubmissionStatus(assignment);
    if (filter === 'all') return true;
    if (filter === 'new') return status === 'Assigned';
    if (filter === 'in-progress') return status === 'In Progress';
    if (filter === 'submitted') return status === 'Submitted';
    if (filter === 'graded') return status === 'Graded' || status === 'Returned';
    if (filter === 'overdue') {
      const dueDate = new Date(assignment.dueDate);
      const now = new Date();
      return dueDate < now && status !== 'Graded' && status !== 'Returned';
    }
    return true;
  });

  const statusColors = {
    'Assigned': '#e8f5e9',
    'In Progress': '#fff3e0',
    'Submitted': '#e8f5e9',
    'Late': '#ffebee',
    'Graded': '#f3e5f5',
    'Returned': '#e0f2f1',
  };

  const statusTextColors = {
    'Assigned': '#15803d',
    'In Progress': '#f57c00',
    'Submitted': '#388e3c',
    'Late': '#d32f2f',
    'Graded': '#7b1fa2',
    'Returned': '#00897b',
  };

  const handleStartAssignment = async (assignment) => {
    try {
      setSubmitting(true);
      const submissionRef = collection(db, 'classes', student.classId, 'assignments', assignment.id, 'assignmentSubmissions');
      const newSubmission = await addDoc(submissionRef, {
        studentId: student.studentId,
        studentName: student.fullName || student.name || '',
        status: 'Started',
        startedAt: new Date().toISOString(),
      });

      // Update local state
      setAssignments(prev => prev.map(a => {
        if (a.id === assignment.id) {
          return {
            ...a,
            submission: {
              assignmentId: assignment.id,
              submissionId: newSubmission.id,
              studentId: student.studentId,
              studentName: student.fullName || student.name || '',
              status: 'Started',
              startedAt: new Date().toISOString(),
            },
          };
        }
        return a;
      }));

      setExpandedId(assignment.id);
      setResponseText('');
    } catch (error) {
      console.error('Error starting assignment:', error);
      alert('Failed to start assignment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAssignment = async (assignment) => {
    if (!responseText.trim()) {
      alert('Please enter your response before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      const submissionDocRef = doc(db, 'classes', student.classId, 'assignments', assignment.id, 'assignmentSubmissions', assignment.submission.submissionId);
      await updateDoc(submissionDocRef, {
        studentResponse: responseText.trim(),
        status: 'Submitted',
        submittedAt: new Date().toISOString(),
      });

      // Update local state
      setAssignments(prev => prev.map(a => {
        if (a.id === assignment.id) {
          return {
            ...a,
            submission: {
              ...a.submission,
              studentResponse: responseText.trim(),
              status: 'Submitted',
              submittedAt: new Date().toISOString(),
            },
          };
        }
        return a;
      }));

      setExpandedId(null);
      setResponseText('');
    } catch (error) {
      console.error('Error submitting assignment:', error);
      alert('Failed to submit assignment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleExpand = (assignment) => {
    if (expandedId === assignment.id) {
      setExpandedId(null);
    } else {
      setExpandedId(assignment.id);
      // Pre-fill response text if there's an existing response
      if (assignment.submission && assignment.submission.studentResponse) {
        setResponseText(assignment.submission.studentResponse);
      } else {
        setResponseText('');
      }
    }
  };

  return (
    <div className="student-assignments-view">
      <h2>Assignments</h2>

      {loading ? (
        <div className="loading">Loading assignments...</div>
      ) : assignments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3>No Assignments</h3>
          <p>Assignments from your teachers will appear here.</p>
        </div>
      ) : (
        <>
          <div className="filter-bar">
            <button
              className={`filter-button ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-button ${filter === 'new' ? 'active' : ''}`}
              onClick={() => setFilter('new')}
            >
              New
            </button>
            <button
              className={`filter-button ${filter === 'in-progress' ? 'active' : ''}`}
              onClick={() => setFilter('in-progress')}
            >
              In Progress
            </button>
            <button
              className={`filter-button ${filter === 'submitted' ? 'active' : ''}`}
              onClick={() => setFilter('submitted')}
            >
              Submitted
            </button>
            <button
              className={`filter-button ${filter === 'graded' ? 'active' : ''}`}
              onClick={() => setFilter('graded')}
            >
              Graded
            </button>
            <button
              className={`filter-button ${filter === 'overdue' ? 'active' : ''}`}
              onClick={() => setFilter('overdue')}
            >
              Overdue
            </button>
          </div>

          <div className="assignments-list">
            {filteredAssignments.map(assignment => {
              const status = getSubmissionStatus(assignment);
              const isExpanded = expandedId === assignment.id;
              const canStart = status === 'Assigned';
              const canSubmit = status === 'In Progress';
              const showFeedback = status === 'Graded' || status === 'Returned';
              return (
                <div
                  key={assignment.id}
                  className={`assignment-card ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => handleToggleExpand(assignment)}
                >
                  <div className="assignment-header">
                    <h3>{assignment.title}</h3>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: statusColors[status],
                        color: statusTextColors[status],
                      }}
                    >
                      {status}
                    </span>
                  </div>
                  <p className="description">{assignment.description || 'No description'}</p>
                  <div className="assignment-meta">
                    <span>📅 Due: {new Date(assignment.dueDate).toLocaleDateString()}</span>
                    {assignment.subject && <span>📖 {assignment.subject}</span>}
                    {assignment.maxScore && <span>📊 Max: {assignment.maxScore}</span>}
                  </div>

                  {isExpanded && (
                    <div className="assignment-expanded" onClick={(e) => e.stopPropagation()}>
                      {canStart && (
                        <div className="start-section">
                          <p className="expand-hint">Click "Start Assignment" to begin working on this assignment.</p>
                          <button
                            className="action-button start-button"
                            onClick={() => handleStartAssignment(assignment)}
                            disabled={submitting}
                          >
                            {submitting ? 'Starting...' : 'Start Assignment'}
                          </button>
                        </div>
                      )}

                      {canSubmit && (
                        <div className="submit-section">
                          <label className="response-label">Your Response</label>
                          <textarea
                            className="response-textarea"
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            placeholder="Type your answer here..."
                            rows={6}
                            disabled={submitting}
                          />
                          <button
                            className="action-button submit-button"
                            onClick={() => handleSubmitAssignment(assignment)}
                            disabled={submitting || !responseText.trim()}
                          >
                            {submitting ? 'Submitting...' : 'Submit Assignment'}
                          </button>
                        </div>
                      )}

                      {status === 'Submitted' && (
                        <div className="submission-info">
                          <p><strong>Submitted:</strong> {new Date(assignment.submission.submittedAt).toLocaleString()}</p>
                          {assignment.submission.studentResponse && (
                            <p><strong>Your Response:</strong> {assignment.submission.studentResponse}</p>
                          )}
                          <p className="awaiting-feedback">Awaiting teacher feedback...</p>
                        </div>
                      )}

                      {status === 'Late' && (
                        <div className="submission-info">
                          <p><strong>Submitted (Late):</strong> {new Date(assignment.submission.submittedAt).toLocaleString()}</p>
                          {assignment.submission.studentResponse && (
                            <p><strong>Your Response:</strong> {assignment.submission.studentResponse}</p>
                          )}
                          <p className="awaiting-feedback">Awaiting teacher feedback...</p>
                        </div>
                      )}

                      {showFeedback && (
                        <div className="submission-info feedback-info">
                          <p><strong>Submitted:</strong> {new Date(assignment.submission.submittedAt).toLocaleString()}</p>
                          {assignment.submission.studentResponse && (
                            <p><strong>Your Response:</strong> {assignment.submission.studentResponse}</p>
                          )}
                          {assignment.submission.teacherFeedback && (
                            <p><strong>Teacher Feedback:</strong> {assignment.submission.teacherFeedback}</p>
                          )}
                          {assignment.submission.score !== undefined && assignment.submission.score !== null && (
                            <p><strong>Score:</strong> {assignment.submission.score}{assignment.maxScore ? ` / ${assignment.maxScore}` : ''}</p>
                          )}
                          {assignment.submission.grade && (
                            <p><strong>Grade:</strong> {assignment.submission.grade}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {!isExpanded && assignment.submission && (
                    <div className="submission-info collapsed-info">
                      {assignment.submission.teacherFeedback && (showFeedback) && (
                        <p><strong>Feedback:</strong> {assignment.submission.teacherFeedback}</p>
                      )}
                      {assignment.submission.score !== undefined && assignment.submission.score !== null && showFeedback && (
                        <p><strong>Score:</strong> {assignment.submission.score}{assignment.maxScore ? ` / ${assignment.maxScore}` : ''}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default StudentAssignmentsView;
